/**
 * IndexedDB cache for TTS audio.
 *
 * Cache key = sha256(language + '|' + voice + '|' + normalized text).
 * Value = WAV Blob (server's response).
 *
 * Why IndexedDB and not localStorage: WAVs are 100s of KB; localStorage
 * has a ~5 MB total cap and stores only strings (would force base64).
 * IndexedDB is unbounded-ish (browser may evict under disk pressure) and
 * stores Blobs natively.
 *
 * Eviction: simple LRU with `maxEntries`. Since chat replies repeat heavily
 * for greetings/acks/common phrases, even a small cache (~500) covers
 * the long tail of frequent utterances.
 */

import type { TtsLanguage } from './server';

const DB_NAME = 'raza-tts-cache';
const STORE_NAME = 'utterances';
const DB_VERSION = 1;
const DEFAULT_MAX_ENTRIES = 1000;

interface CacheRecord {
  key: string;
  wav: Blob;
  createdAt: number;
  lastUsed: number;
  hits: number;
  /** Stored for debugging — not part of the key. */
  meta: { language: TtsLanguage; text: string };
}

let dbPromise: Promise<IDBDatabase> | null = null;

// After this many consecutive failures, stop attempting IDB operations for the
// rest of the session. Some users (private browsing, full storage quota,
// browser bugs) have permanently broken IDB; retrying every TTS playback wastes
// network round-trips and floods telemetry. Reset on successful operation.
const FAILURE_THRESHOLD = 5;
let consecutiveFailures = 0;
let idbDisabled = false;

function recordFailure(reason: string) {
  consecutiveFailures++;
  if (!idbDisabled && consecutiveFailures >= FAILURE_THRESHOLD) {
    idbDisabled = true;
    // Disable is the recovery — common on iOS Safari ITP eviction + private mode.
    // Use info so it doesn't surface as an admin warning.
    console.info(`[ttsCache] disabling IDB for session — ${FAILURE_THRESHOLD} consecutive failures (last: ${reason})`);
  }
}
function recordSuccess() {
  if (consecutiveFailures > 0) consecutiveFailures = 0;
}

function getDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  // Some embedded webviews ship without IndexedDB entirely. Fail fast with a
  // typed rejection instead of throwing a raw ReferenceError that would
  // surface as "Can't find variable: indexedDB" in the admin error panel.
  if (typeof indexedDB === 'undefined') {
    idbDisabled = true;
    return Promise.reject(new Error('IndexedDB unavailable in this browser'));
  }
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('lastUsed', 'lastUsed', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IDB open failed'));
  });
  return dbPromise;
}

async function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => Promise<T> | T): Promise<T> {
  const db = await getDb();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE_NAME, mode);
    const store = t.objectStore(STORE_NAME);
    let result: T;
    Promise.resolve(fn(store))
      .then((r) => { result = r; })
      .catch(reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error ?? new Error('IDB tx failed'));
    t.onabort = () => reject(t.error ?? new Error('IDB tx aborted'));
  });
}

function idbReq<T = any>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/* ── Key derivation ───────────────────────────────────────────────── */

function normalizeText(text: string): string {
  // Trim, collapse whitespace, lowercase. Punctuation kept (changes prosody).
  return text.trim().replace(/\s+/g, ' ').toLowerCase();
}

export async function cacheKey(language: TtsLanguage, voice: string, text: string): Promise<string> {
  const data = `${language}|${voice}|${normalizeText(text)}`;
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(data));
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
}

/* ── Public API ───────────────────────────────────────────────────── */

export async function cacheGet(key: string): Promise<Blob | null> {
  if (idbDisabled) return null;
  try {
    const result = await tx('readwrite', async (store) => {
      const rec = await idbReq<CacheRecord | undefined>(store.get(key));
      if (!rec) return null;
      // Update lastUsed + hits (LRU). Best-effort — don't await result.
      rec.lastUsed = Date.now();
      rec.hits = (rec.hits ?? 0) + 1;
      store.put(rec);
      return rec.wav;
    });
    recordSuccess();
    return result;
  } catch (err) {
    // IDB failures are tracked via recordFailure(); disable triggers after threshold.
    console.info('[ttsCache] get failed', err);
    recordFailure('get');
    return null;
  }
}

export async function cacheSet(
  key: string,
  wav: Blob,
  language: TtsLanguage,
  text: string,
  maxEntries = DEFAULT_MAX_ENTRIES,
): Promise<void> {
  if (idbDisabled) return;
  try {
    await tx('readwrite', async (store) => {
      const now = Date.now();
      const rec: CacheRecord = {
        key,
        wav,
        createdAt: now,
        lastUsed: now,
        hits: 0,
        meta: { language, text },
      };
      store.put(rec);
    });
    recordSuccess();
    await evictIfOver(maxEntries);
  } catch (err) {
    console.info('[ttsCache] set failed', err);
    recordFailure('set');
  }
}

/** Evict the least-recently-used entries until count ≤ maxEntries. */
async function evictIfOver(maxEntries: number): Promise<void> {
  try {
    await tx('readwrite', async (store) => {
      const total = await idbReq<number>(store.count());
      if (total <= maxEntries) return;
      const toDelete = total - maxEntries;
      // Walk lastUsed index in ascending order, delete the first N.
      const idx = store.index('lastUsed');
      let deleted = 0;
      const cursorReq = idx.openCursor(null, 'next');
      await new Promise<void>((resolve, reject) => {
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor || deleted >= toDelete) { resolve(); return; }
          cursor.delete();
          deleted++;
          cursor.continue();
        };
        cursorReq.onerror = () => reject(cursorReq.error);
      });
    });
  } catch (err) {
    console.info('[ttsCache] eviction failed', err);
  }
}

/** For debugging / admin: how many entries + total bytes. */
export async function cacheStats(): Promise<{ count: number; estimatedBytes: number }> {
  try {
    return await tx('readonly', async (store) => {
      const records = await idbReq<CacheRecord[]>(store.getAll());
      const estimatedBytes = records.reduce((sum, r) => sum + (r.wav?.size ?? 0), 0);
      return { count: records.length, estimatedBytes };
    });
  } catch {
    return { count: 0, estimatedBytes: 0 };
  }
}

/** Wipe everything. Use sparingly. */
export async function cacheClear(): Promise<void> {
  await tx('readwrite', (store) => idbReq(store.clear()));
}

/* ── Static phrase pack pre-population ──────────────────────────────
 *
 * On app boot, fetch the manifest from /tts-cache/manifest.json. For any
 * entry whose key is NOT already in IndexedDB, fetch the WAV file and
 * insert it. This way:
 *   - First app load: ~40 small fetches in parallel from /public/. Fast.
 *   - Subsequent loads: zero fetches (entries already in IDB).
 *
 * Idempotent: safe to call from app startup multiple times. The cache key
 * derivation matches the runtime path, so cache hits work transparently.
 */

interface PhrasePackManifest {
  generatedAt: string;
  entries: Array<{ key: string; language: 'English' | 'Spanish'; text: string; filename: string }>;
}

let prepopulationPromise: Promise<{ added: number; skipped: number }> | null = null;

export function prepopulateFromManifest(manifestUrl = '/tts-cache/manifest.json'): Promise<{ added: number; skipped: number }> {
  if (prepopulationPromise) return prepopulationPromise;
  prepopulationPromise = (async () => {
    let added = 0;
    let skipped = 0;
    try {
      const res = await fetch(manifestUrl, { cache: 'force-cache' });
      if (!res.ok) {
        console.debug(`[ttsCache] no phrase manifest at ${manifestUrl} (${res.status}) — skipping`);
        return { added: 0, skipped: 0 };
      }
      // Some hosts return a 200 with an HTML 404 page when the static asset
      // isn't deployed. Catch that before .json() throws "Unexpected token '<'".
      const contentType = res.headers.get('content-type') ?? '';
      if (!contentType.includes('json')) {
        console.debug(`[ttsCache] manifest at ${manifestUrl} is not JSON (${contentType}) — skipping`);
        return { added: 0, skipped: 0 };
      }
      const manifest: PhrasePackManifest = await res.json();

      // Get all existing keys in one batch read
      const existingKeys = await tx('readonly', async (store) => {
        const keys = await idbReq<IDBValidKey[]>(store.getAllKeys());
        return new Set(keys.map(String));
      });

      // Fetch missing entries in parallel — they're tiny static files
      await Promise.all(manifest.entries.map(async (entry) => {
        if (existingKeys.has(entry.key)) {
          skipped++;
          return;
        }
        try {
          const wavRes = await fetch(`/tts-cache/${entry.filename}`, { cache: 'force-cache' });
          if (!wavRes.ok) {
            console.warn(`[ttsCache] failed to fetch ${entry.filename}: ${wavRes.status}`);
            return;
          }
          const wav = await wavRes.blob();
          await cacheSet(entry.key, wav, entry.language as TtsLanguage, entry.text);
          added++;
        } catch (err) {
          console.warn(`[ttsCache] prepop entry failed for "${entry.text.slice(0, 30)}":`, err);
        }
      }));

      if (added > 0) {
        console.log(`[ttsCache] pre-populated ${added} phrase WAVs (${skipped} already cached)`);
      }
      return { added, skipped };
    } catch (err) {
      // Common on browsers without IndexedDB (private mode, in-app
      // webviews) — informational, the runtime path skips the cache and
      // synthesizes on demand.
      console.info('[ttsCache] phrase pack pre-population skipped', err);
      return { added: 0, skipped: 0 };
    }
  })();
  return prepopulationPromise;
}
